import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Maximize2Icon, MinusIcon, PlusIcon } from 'lucide-react';
import { Button } from '@tinct/web-components';
import { OverlayHostContext } from '../../../landing-page-editor/components/html-block-selector/hooks';
import { useCanvasTransform } from './use-canvas-transform';

/**
 * What the canvas needs from a wheel event. `clientX`/`clientY` are WINDOW
 * coordinates: an event captured inside an artboard iframe must be mapped
 * out of the iframe's own (unscaled) space before it gets here.
 */
export type CanvasWheelInput = {
  deltaX: number;
  deltaY: number;
  ctrlKey: boolean;
  metaKey: boolean;
  clientX: number;
  clientY: number;
};

type StudioCanvasHelpers = {
  /** Forwarder for wheel events captured inside artboard iframes. */
  onDocumentWheel: (e: CanvasWheelInput) => void;
};

type StudioCanvasProps = {
  children: (helpers: StudioCanvasHelpers) => ReactNode;
};

const ZOOM_WHEEL_SENSITIVITY = 0.01;
const ZOOM_STEP = 1.25;

/**
 * The Framer-style pan/zoom viewport. Artboards live on a transformed layer
 * (translate + scale, origin 0 0); because every block overlay is portaled
 * INTO its iframe's document, scaling the layer scales artboards, overlays
 * and all — no coordinate math anywhere else.
 *
 * Interactions: wheel pans, ctrl/cmd+wheel (and trackpad pinch, which
 * browsers report as ctrl+wheel) zooms around the cursor, dragging the
 * background pans, and the pill offers −/+/Fit.
 */
export function StudioCanvas({ children }: StudioCanvasProps) {
  const { zoomPercent, viewportRef, setLayerRef, setContentSize, zoomBy, panBy, fitToView } =
    useCanvasTransform();
  const layerRef = useRef<HTMLDivElement | null>(null);
  const hasAutoFitRef = useRef(false);

  // The viewport doubles as the overlay host (block badges portal into it so
  // they escape the scaled, clipped artboards). State, not a ref: the
  // context must re-render its consumers once the element exists.
  const [overlayHost, setOverlayHost] = useState<HTMLDivElement | null>(null);
  const handleViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      viewportRef.current = node;
      setOverlayHost(node);
    },
    [viewportRef],
  );

  // The transform hook paints the layer through its own callback ref; we also
  // keep a local ref for hit-testing and the size observer.
  const handleLayerRef = useCallback(
    (node: HTMLDivElement | null) => {
      layerRef.current = node;
      setLayerRef(node);
    },
    [setLayerRef],
  );

  const handleWheelEvent = useCallback(
    (e: CanvasWheelInput) => {
      if (e.ctrlKey || e.metaKey) {
        // zoomBy converts window coords internally — no ref access here, so
        // this closure stays safe to hand to render-time children.
        zoomBy(Math.exp(-e.deltaY * ZOOM_WHEEL_SENSITIVITY), e.clientX, e.clientY);
      } else {
        panBy(-e.deltaX, -e.deltaY);
      }
    },
    [zoomBy, panBy],
  );

  // A two-finger horizontal swipe that overscrolls the page is Chrome's
  // back/forward gesture. Preventing the wheel defaults (below) is not
  // enough: the gesture is decided at the root scroller, and a wheel that
  // lands before an artboard iframe's forwarder is bound still chains up to
  // it. Opt the document out while the canvas is mounted; restored on
  // unmount so the rest of the app keeps the gesture.
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overscrollBehaviorX;
    root.style.overscrollBehaviorX = 'none';
    return () => {
      root.style.overscrollBehaviorX = previous;
    };
  }, []);

  // Native wheel listener (passive: false): React's synthetic onWheel is
  // passive, so preventDefault — needed to stop browser page-zoom on
  // ctrl+wheel — wouldn't work through it.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      handleWheelEvent(e);
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [viewportRef, handleWheelEvent]);

  // Drag-to-pan on the canvas background (artboards themselves are iframes —
  // pointer events over them never reach the viewport, so this only engages
  // on empty canvas, which is the intent).
  const dragRef = useRef<{ pointerId: number; lastX: number; lastY: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    // Only empty canvas starts a pan: capturing on child presses would
    // retarget their click to the viewport (the zoom pill would go dead).
    if (e.target !== e.currentTarget && e.target !== layerRef.current) return;
    dragRef.current = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    panBy(e.clientX - drag.lastX, e.clientY - drag.lastY);
    drag.lastX = e.clientX;
    drag.lastY = e.clientY;
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
  };

  // Track the layer's natural size (transform doesn't affect offset sizes)
  // and frame the content once it first has real dimensions.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    const observer = new ResizeObserver(() => {
      setContentSize(layer.offsetWidth, layer.offsetHeight);
      if (!hasAutoFitRef.current && layer.offsetWidth > 0) {
        hasAutoFitRef.current = true;
        fitToView();
      }
    });
    observer.observe(layer);
    return () => observer.disconnect();
  }, [setContentSize, fitToView]);

  // The render prop's output is memoized so a pill-label re-render (the only
  // React render pan/zoom still causes) never re-invokes the artboards.
  const content = useMemo(() => children({ onDocumentWheel: handleWheelEvent }), [children, handleWheelEvent]);

  return (
    <div
      ref={handleViewportRef}
      className="absolute inset-0 cursor-grab touch-none overflow-hidden overscroll-none bg-muted/60 active:cursor-grabbing"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* `transform` itself is written imperatively by useCanvasTransform
          (rAF-coalesced) — see the hook for why it is not React state.
          will-change keeps the layer on its own compositor layer so pans are
          composite-only. */}
      <div
        ref={handleLayerRef}
        className="absolute flex w-max items-start gap-16 will-change-transform"
        style={{ transformOrigin: '0 0' }}
      >
        <OverlayHostContext.Provider value={overlayHost}>{content}</OverlayHostContext.Provider>
      </div>

      {/* Zoom pill — opaque on purpose: a backdrop-blur over a layer that
          moves every frame forces a re-blur per frame. */}
      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-1 rounded-full border border-border bg-background p-1 shadow-lg">
        <Button
          variant="ghost"
          size="icon"
          className="size-8 rounded-full"
          aria-label="Zoom out"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
        >
          <MinusIcon className="size-4" />
        </Button>
        <span className="w-12 text-center font-mono text-xs text-muted-foreground">
          {zoomPercent}%
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 rounded-full"
          aria-label="Zoom in"
          onClick={() => zoomBy(ZOOM_STEP)}
        >
          <PlusIcon className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 rounded-full"
          aria-label="Fit to view"
          onClick={fitToView}
        >
          <Maximize2Icon className="size-4" />
        </Button>
      </div>
    </div>
  );
}
